/**
 * Password Strength Meter Component
 *
 * SECURITY FIX (Phase 3 #1): Now uses shared validation logic
 * Ensures consistency between client and server password validation
 */

import { useMemo } from 'react';
import {
  validatePassword,
  getPasswordRequirements,
  getPasswordStrengthColor,
  getPasswordStrengthTextColor,
} from '../../lib/passwordValidation';

interface PasswordStrengthMeterProps {
  password: string;
  showRequirements?: boolean;
}

export default function PasswordStrengthMeter({
  password,
  showRequirements = true,
}: PasswordStrengthMeterProps) {
  const { requirements, strength, score, color, textColor } = useMemo(() => {
    if (!password) {
      return {
        requirements: [],
        strength: 'weak' as const,
        score: 0,
        color: 'bg-gray-300',
        textColor: 'text-gray-500',
      };
    }

    const validation = validatePassword(password);
    const reqs = getPasswordRequirements(password);

    return {
      requirements: reqs,
      strength: validation.strength,
      score: validation.score,
      color: getPasswordStrengthColor(validation.strength),
      textColor: getPasswordStrengthTextColor(validation.strength),
    };
  }, [password]);

  if (!password) {
    return null;
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-gray-600">Password strength</span>
          <span className={`capitalize ${textColor}`}>
            {strength}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${color}`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      {/* Requirements list */}
      {showRequirements && (
        <ul className="text-xs space-y-1">
          {requirements.map((req, index) => (
            <li
              key={index}
              className={`flex items-center gap-1.5 ${
                req.met ? 'text-green-600' : 'text-gray-500'
              }`}
            >
              {req.met ? (
                <svg
                  className="w-3.5 h-3.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                </svg>
              )}
              {req.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
